import React, { useId, useState } from "react";
import { IconButton, Popover } from "@mui/material";
import { FaInfoCircle, FaTimes } from "react-icons/fa";
import "./MicoInformation.css";

const nombre = (value) => Number(value).toLocaleString("fr-FR", { maximumFractionDigits: 2 });
const euros = (value) => `${Number(value).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

const Ligne = ({ libelle, children }) => (
  <div className="mico-information-ligne">
    <dt>{libelle}</dt>
    <dd>{children}</dd>
  </div>
);

const MicoInformation = ({ infosPension }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const id = useId();
  const mico = infosPension?.InfosRetraite?.Mico;
  const regime = infosPension?.InfosRetraite?.RegimeUnifie;
  const detail = mico?.DetailCalcul;
  const open = Boolean(anchorEl);
  if (!mico) return null;

  const tauxEnfant = detail?.TauxMajorationEnfant ?? regime?.CoeffMajoEnfant ?? 0;
  const pensionBase = detail?.PensionBaseTheorique ?? regime?.PensionTheorique;
  const complement = detail?.ComplementAvantMajorationEnfant ?? (mico.micoCalcul != null && pensionBase != null ? Math.max(0, mico.micoCalcul - pensionBase) : null);
  const dureeReference = detail?.DureeReference ?? infosPension?.Infos?.DureeReference;
  const micoNul = mico.Pension != null && Number(mico.Pension) === 0;
  const motifsMicoNul = [];
  if (micoNul) {
    if (pensionBase != null && mico.micoCalcul != null && Number(pensionBase) >= Number(mico.micoCalcul)) {
      motifsMicoNul.push(`La pension de base théorique (${euros(pensionBase)}) atteint ou dépasse le minimum calculé (${euros(mico.micoCalcul)}). Aucun complément n’est nécessaire.`);
    }
    if (detail?.TotalPensionsAvantMico != null && detail?.PlafondPensions != null && Number(detail.TotalPensionsAvantMico) >= Number(detail.PlafondPensions)) {
      motifsMicoNul.push(`Le total des pensions pris en compte (${euros(detail.TotalPensionsAvantMico)}) atteint ou dépasse le plafond annuel (${euros(detail.PlafondPensions)}). Le plafonnement ne permet aucun versement de MICO.`);
    }
    if (complement > 0 && detail?.MicoAvantEcretement != null && Number(detail.MicoAvantEcretement) === 0) {
      motifsMicoNul.push("Le complément calculé après majoration de pension pour enfants est ramené à 0 € par l’arrondi à l’euro.");
    }
  }
  const close = () => setAnchorEl(null);

  return (
    <>
      <IconButton
        size="small"
        className="mico-information-bouton"
        aria-label="Détail du minimum contributif"
        title="Détail du minimum contributif"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        onClick={(event) => setAnchorEl(event.currentTarget)}
      >
        <FaInfoCircle aria-hidden="true" size={16} />
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={close}
        // Les fenêtres de l'application utilisent les niveaux 10000 et 10001.
        sx={{ zIndex: 10002 }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ className: "mico-information-popover" }}
      >
        <section id={id} role="dialog" tabIndex={-1} aria-labelledby={`${id}-titre`} className="mico-information">
          <div className="mico-information-entete">
            <h3 id={`${id}-titre`}>Détail du minimum contributif</h3>
            <IconButton size="small" aria-label="Fermer le détail du MICO" onClick={close}>
              <FaTimes aria-hidden="true" size={14} />
            </IconButton>
          </div>
          <p>Le MICO complète la pension de base du régime unifié. Les calculs ci-dessous sont annuels et bruts.</p>

          {micoNul && (
            <section className="mico-information-absence" aria-labelledby={`${id}-absence`}>
              <h4 id={`${id}-absence`}>Aucun complément MICO versé</h4>
              <p>Le montant versé pour ce départ est de <strong>0 €</strong>.</p>
              {motifsMicoNul.length > 0 ? (
                <ul>{motifsMicoNul.map((motif) => <li key={motif}>{motif}</li>)}</ul>
              ) : (
                <p>Aucun complément n’est versé pour les paramètres de ce départ.</p>
              )}
              <p>Les montants du MICO de base et du MICO majoré ci-dessous présentent le minimum calculé. Le complément effectivement versé est nul.</p>
            </section>
          )}

          <section className="mico-information-section" aria-labelledby={`${id}-base`}>
            <h4 id={`${id}-base`}>MICO de base</h4>
            <h5>Informations</h5>
            <p>Le MICO de base est proratisé selon la durée retenue au régime unifié et la durée de référence ou le total des durées tous régimes.</p>
            <dl>
              {detail && <Ligne libelle="Montant annuel de référence du MICO de base">{euros(detail.MontantReferenceSimple)}</Ligne>}
              {mico.trimRG != null && <Ligne libelle="Durée retenue pour le MICO">{nombre(mico.trimRG)} trimestres</Ligne>}
              {detail?.TrimestresMajorationEnfant > 0 && (
                <Ligne libelle="Dont majoration de durée pour enfants">{nombre(detail.TrimestresMajorationEnfant)} trimestres</Ligne>
              )}
              {dureeReference != null && <Ligne libelle="Durée de référence">{nombre(dureeReference)} trimestres</Ligne>}
              {detail && <Ligne libelle="Total tous régimes pour le prorata">{nombre(detail.TotalTrimestreMico)} trimestres</Ligne>}
            </dl>
            <h5>Calcul</h5>
            <dl>
              {mico.micoSimple != null && (
                <Ligne libelle="Minimum contributif simple">
                  {detail && <span className="mico-information-formule">{euros(detail.MontantReferenceSimple)} × {nombre(detail.TotalTrimestreMico > dureeReference ? mico.trimRG : Math.min(dureeReference, mico.trimRG))} / {nombre(detail.TotalTrimestreMico > dureeReference ? detail.TotalTrimestreMico : dureeReference)} = </span>}
                  {euros(mico.micoSimple)}
                </Ligne>
              )}
            </dl>
          </section>

          <section className="mico-information-section" aria-labelledby={`${id}-majore`}>
            <h4 id={`${id}-majore`}>MICO majoré</h4>
            <h5>Informations</h5>
            <p>Le MICO majoré est calculé à partir de 120 trimestres cotisés.</p>
            <dl>
              {detail && <Ligne libelle="Montant annuel de référence de la majoration">{euros(detail.MontantReferenceMajoration)}</Ligne>}
              {detail && <Ligne libelle="Trimestres cotisés au régime unifié, hors AVPF">{nombre(detail.TrimestresCotisesRU)}</Ligne>}
              {detail && <Ligne libelle="AVPF retenue dans la limite de 24">{nombre(detail.TrimestresAvpfRetenus)}</Ligne>}
              {mico.TrimCotisesMicoMajore != null && (
                <Ligne libelle="Trimestres du régime unifié retenus pour la majoration">{nombre(mico.TrimCotisesMicoMajore)}</Ligne>
              )}
            </dl>
            {detail && (
              <p>
                Le compteur tous régimes pour la majoration est de {nombre(detail.TotalTrimestreMicoMajore)} trimestres :
                {detail.TotalTrimestreMicoMajore >= 120 ? " le seuil de 120 trimestres est atteint." : " le seuil de 120 trimestres n’est pas atteint, aucune majoration du MICO ne s’applique."}
              </p>
            )}
            <h5>Calcul</h5>
            <dl>
              {mico.micoMajore != null && (
                <Ligne libelle="Majoration du minimum contributif">
                  {detail && detail.TotalTrimestreMicoMajore >= 120 && (
                    <span className="mico-information-formule">
                      {euros(detail.MontantReferenceMajoration)}
                      {detail.TotalTrimestreMicoMajore <= dureeReference && <> × {nombre(mico.TrimCotisesMicoMajore)} / {nombre(dureeReference)}</>}
                      {" × "}{nombre(mico.trimRG)} / {nombre(detail.TotalTrimestreMico)} = {" "}
                    </span>
                  )}
                  {euros(mico.micoMajore)}
                </Ligne>
              )}
              {mico.micoCalcul != null && (
                <Ligne libelle="Minimum contributif majoré">
                  {mico.micoSimple != null && mico.micoMajore != null && (
                    <span className="mico-information-formule">{euros(mico.micoSimple)} + {euros(mico.micoMajore)} = </span>
                  )}
                  {euros(mico.micoCalcul)}
                </Ligne>
              )}
            </dl>
          </section>

          <section className="mico-information-section" aria-labelledby={`${id}-complement`}>
            <h4 id={`${id}-complement`}>Complément versé</h4>
            <p>Le complément correspond à la différence entre le minimum à atteindre et la pension de base théorique, puis tient compte de la majoration de pension pour enfants et du plafonnement.</p>
            <dl>
              {mico.micoCalcul != null && <Ligne libelle="Minimum à atteindre avant majoration pour enfants">{euros(mico.micoCalcul)}</Ligne>}
              {pensionBase != null && <Ligne libelle="Pension de base théorique à déduire">− {euros(pensionBase)}</Ligne>}
              {complement != null && (
                <Ligne libelle="Complément avant majoration pour enfants">
                  {micoNul && mico.micoCalcul != null && pensionBase != null && (
                    <span className="mico-information-formule">Max(0 ; {euros(mico.micoCalcul)} − {euros(pensionBase)}) = </span>
                  )}
                  {euros(complement)}
                </Ligne>
              )}
              <Ligne libelle="Majoration de pension pour enfants">{nombre(tauxEnfant)} %</Ligne>
              {detail && (
                <Ligne libelle="Complément brut arrondi avant plafonnement">
                  <span className="mico-information-formule">Arrondi de {euros(complement)} × (1 + {nombre(tauxEnfant)} %) = </span>
                  {euros(detail.MicoAvantEcretement)}
                </Ligne>
              )}
            </dl>

            {detail && (
              <>
                <h5>Plafonnement</h5>
                <dl>
                  <Ligne libelle="Pensions prises en compte avant MICO (hors surcote du régime unifié)">{euros(detail.TotalPensionsAvantMico)}</Ligne>
                  <Ligne libelle="Plafond annuel des pensions">{euros(detail.PlafondPensions)}</Ligne>
                  <Ligne libelle="Réduction du MICO pour dépassement du plafond">{detail.Ecretement > 0 ? `− ${euros(detail.Ecretement)}` : "Aucun écrêtement"}</Ligne>
                </dl>
              </>
            )}

            <div className="mico-information-resultat">
              <strong>MICO brut : {euros(mico.Pension)} / an</strong>
              <span>Soit {euros(mico.Pension / 12)} / mois</span>
              {mico.PensionNette != null && <span>MICO net de prélèvements sociaux{detail ? ` (${nombre(detail.TauxPrelevementsSociaux)} %)` : ""} : {euros(mico.PensionNette)} / an</span>}
            </div>
          </section>
        </section>
      </Popover>
    </>
  );
};
export default MicoInformation;
